import { FC, useContext, useEffect, useState } from 'react';

import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { Button, CircleProgressBar } from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { CashboxContext, useIsCashboxEnabled } from 'cashbox';
import { LocalizationContext } from 'localization';
import { isSandbox, refreshSandboxViews } from 'sandbox';
import {
  ONBOARDING_PAYMENT_REFUNDED_EVENT,
  OnboardingPaymentRefundedDetail,
} from 'sandbox/onboarding/events';
import { REJECT_PAYMENT, VALIDATE_REFUND } from 'schemas';
import notification from 'services/notificationService';
import { SettingsContext } from 'settings';
import { EPaymentItemType, EPaymentMethod, TPayment } from 'types';

import { GET_ACTIVE_PAYMENTS } from '../schema';
import s from './RefundButton.module.scss';
import { getRefundRemainingSeconds } from './refundEligibility';
import { RejectPaymentModal } from './RefundPaymentModal';

export interface IRejectButtonProps {
  payment: TPayment;
  bordered?: boolean;
  disabled?: boolean;
  onlyIcon?: boolean;
}

export const RefundButton: FC<IRejectButtonProps> = ({
  payment,
  bordered,
  disabled,
  onlyIcon,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const {
    state: {
      settings: { isRefundEnabled, refundHeadroom },
    },
  } = useContext(SettingsContext);
  const isCashboxEnabled = useIsCashboxEnabled();
  const { refundPayment: cashBoxRefund, loadKKMs } = useContext(CashboxContext);
  const apolloClient = useApolloClient();

  const refreshRefundViews = async (): Promise<void> => {
    if (!isSandbox) return;
    await refreshSandboxViews(apolloClient, [
      'payments',
      'shop',
      'hosts',
      'sessions',
      'clients',
      'logs',
    ]);
  };

  const handleRefundCompleted = async (): Promise<void> => {
    await refreshRefundViews();
    notification.info({
      t,
      title: t('payment.notify.refunded'),
      type: 'success',
      dismiss: 5,
    });

    if (isSandbox) {
      window.dispatchEvent(
        new CustomEvent<OnboardingPaymentRefundedDetail>(
          ONBOARDING_PAYMENT_REFUNDED_EVENT,
          { detail: { paymentId: payment.id } }
        )
      );
    }
  };

  const [refundPayment] = useMutation(REJECT_PAYMENT, {
    awaitRefetchQueries: true,
    refetchQueries: !permissions?.has('work_shift_active')
      ? [GET_ACTIVE_PAYMENTS]
      : [],
  });

  const [validateRefund] = useLazyQuery(VALIDATE_REFUND, {
    fetchPolicy: 'network-only',
  });

  const [restTime, setRestTime] = useState(0);
  const [isRejectPaymentModalOpen, setIsRejectPaymentModalOpen] =
    useState(false);

  useEffect(() => {
    if (!isRefundEnabled || !payment.created_at) return;

    const remainingTime = getRefundRemainingSeconds(
      payment.created_at,
      refundHeadroom
    );

    if (remainingTime <= 0) {
      setRestTime(0);
      return;
    }

    setRestTime(remainingTime);

    const interval = setInterval(() => {
      setRestTime((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [payment.created_at, isRefundEnabled, refundHeadroom]);

  if (restTime <= 0) {
    return null;
  }

  return (
    <div
      data-onboarding="payment-refund"
      data-onboarding-payment-id={payment.id}
      className={cn(s.onboarding_target, onlyIcon && s.only_icon_target)}
    >
      <Button
        variant={bordered ? 'outline' : 'ghost'}
        fullWidth
        color="red"
        className={cn(s.root, onlyIcon && s.only_icon)}
        onClick={(event) => {
          event.stopPropagation();
          setIsRejectPaymentModalOpen(true);
        }}
        disabled={disabled}
      >
        <CircleProgressBar
          progress={
            ((refundHeadroom * 60 - restTime) / (refundHeadroom * 60)) * 100
          }
          total={100}
          size={16}
          strokeWidth={2}
        />

        {!onlyIcon && t('payment.refund.btn')}
        {isRejectPaymentModalOpen && !onlyIcon && (
          <RejectPaymentModal
            closeModal={() => setIsRejectPaymentModalOpen(false)}
            onClick={() => {
              const printableItems = payment.items.filter(
                (item) =>
                  item.type !== EPaymentItemType.TARIFF ||
                  item.entity?.disable_sale_receipt_printing === false
              );

              if (
                isCashboxEnabled &&
                payment.method !== EPaymentMethod.BONUS &&
                payment.method !== EPaymentMethod.DEPOSIT
              ) {
                return loadKKMs().then(() =>
                  validateRefund({
                    variables: {
                      input: {
                        id: payment.id,
                      },
                    },
                  })
                    .then((res) => {
                      if (res.error) {
                        throw res.error;
                      }

                      if (!res.data?.validateRefund) {
                        throw new Error('Unknown error occurred');
                      }

                      return cashBoxRefund({
                        items: printableItems,
                        sum: printableItems.reduce(
                          (acc, item) => acc + item.sum,
                          0
                        ),
                        method: payment.method,
                        card_sum: payment.card_sum,
                        cash_sum: payment.cash_sum,
                      });
                    })
                    .then(() =>
                      refundPayment({
                        variables: {
                          input: {
                            id: payment.id,
                          },
                        },
                      })
                    )
                    .then(handleRefundCompleted)
                    .catch((error) => {
                      if (
                        error?.message.includes(
                          'client session seance is not last'
                        )
                      ) {
                        notification.info({
                          t,
                          type: 'error',
                          title: t('payment.notify.error'),
                          message: t('payment.notify.error.direction'),
                        });
                      }

                      if (error?.message.includes('not enough deposit')) {
                        notification.info({
                          t,
                          type: 'error',
                          dismiss: 3,
                          title: t('payment.notify.error'),
                          message: t('payment.notify.error.deposit'),
                        });
                      }
                    })
                );
              }

              return refundPayment({
                variables: {
                  input: {
                    id: payment.id,
                  },
                },
              })
                .then(handleRefundCompleted)
                .catch((error) => {
                  if (error.message === 'client session seance is not last') {
                    notification.info({
                      t,
                      type: 'error',
                      title: t('payment.notify.error'),
                      message: t('payment.notify.error.direction'),
                    });
                  }

                  if (error.message === 'not enough deposit') {
                    notification.info({
                      t,
                      type: 'error',
                      dismiss: 3,
                      title: t('payment.notify.error'),
                      message: t('payment.notify.error.deposit'),
                    });
                  }
                });
            }}
          />
        )}
      </Button>
    </div>
  );
};
